import React, { useState } from 'react';
import { 
  Grid, CardContent, CardMedia, Typography, ThemeProvider, Chip, Box, 
  Tabs, Tab, Paper, Table, 
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Modal, Backdrop, Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { catTheme, BreedCard, styles } from './GuideTheme';

// Helper function to get images
function getImage(name) {
  try {
    return require(`./images/breeds/${name}.jpeg`);
  } catch (e) {
    return require(`./images/not_found.png`); // fallback image
  }
}

// 猫咪品种数据
const catBreeds = {
  chineseBreeds: [
    {
      name: '中国狸花猫',
      image: getImage('中国狸花猫'),
      traits: ['虎斑状被毛', '体格健壮', '捕鼠能力强'],
      origin: '中国',
      weight: '4-7kg',
      description: '中国狸花猫拥有虎斑状被毛，体格健壮，捕鼠能力强。它是唯一被国际猫协会（CFA）认可的中国本土猫品种。'
    },
    {
      name: '山东狮子猫',
      image: getImage('山东狮子猫'),
      traits: ['长毛', '通常为白色', '异色瞳'],
      origin: '中国',
      weight: '4-7kg',
      description: '山东狮子猫（也称临清狮猫）特征是长毛，通常为白色，部分具有异色瞳（鸳鸯眼）。据说源于波斯猫与鲁西狸猫的杂交，曾是皇室宠物。'
    },
    {
      name: '四川简州猫',
      image: getImage('四川简州猫'),
      traits: ['耳朵轮廓重叠', '似有四耳', '体型健壮'],
      origin: '中国',
      weight: '4-7kg',
      description: '四川简州猫（四耳猫）的特征是耳朵轮廓重叠，似有四耳，体型健壮。在明清时期作为贡品，现已较为稀有。'
    },
    {
      name: '玄猫',
      image: getImage('玄猫'),
      traits: ['全身黑色', '被视为灵猫', '象征辟邪和吉祥'],
      origin: '中国',
      weight: '4-7kg',
      description: '玄猫（黑猫）的特征是全身黑色，被视为灵猫，象征辟邪和吉祥。在中国传统文化中，黑猫常被认为具有神秘力量。'
    },
    {
      name: '黄狸猫',
      image: getImage('黄狸猫'),
      traits: ['橘黄色被毛', '性格温顺', '食量大'],
      origin: '中国',
      weight: '4-7kg',
      description: '黄狸猫（橘猫）的特征是橘黄色被毛，性格温顺，食量大。因其亲人和易于饲养的特性，深受欢迎。'
    },
    {
      name: '三花猫',
      image: getImage('三花猫'),
      traits: ['三色被毛', '多为雌性', '被视为幸运'],
      origin: '中国',
      weight: '4-7kg',
      description: '三花猫/玳瑁猫通常拥有黑、白、橙三色被毛，大多数为雌性。在中国传统文化中，三花猫被视为能带来好运的吉祥物。'
    }
  ],
  shortHair: [
    {
      name: '英国短毛猫',
      image: getImage('英国短毛猫'),
      traits: ['圆脸短吻', '双层被毛', '温顺亲人'],
      origin: '英国',
      weight: '4-8kg',
      description: '英国短毛猫是一种体型矮胖、结实的猫，拥有浓密而柔软的短被毛，摸起来像泰迪熊一样。它们的毛色种类繁多，其中最著名的是蓝色，即纯灰色的品种。这种猫以其随和、深情和友好的性格而著称。与一些更活跃的品种相比，英国短毛猫通常比较平静和安静，这使得它们非常适合公寓生活。它们与儿童和其他宠物相处融洽。虽然它们的被毛是短的，但由于非常浓密，因此仍然需要定期梳理。由于它们的活动量相对较低，因此需要特别注意控制它们的饮食以防止体重增加。'
    },
    {
      name: '美国短毛猫',
      image: getImage('美国短毛猫'),
      traits: ['银色虎斑', '肌肉发达', '健康长寿'],
      origin: '美国',
      weight: '3.5-7kg',
      description: '美国短毛猫是一种体格健壮、肌肉发达的猫。它们拥有短而浓密的被毛，颜色和图案非常多样，其中银色虎斑是最受欢迎的颜色之一。这种猫以其温顺、友善和适应性强的性格而闻名，使其成为理想的家庭宠物。它们通常具有良好的性情和聪明的头脑，并且能够与儿童和其他宠物友好相处。美国短毛猫的历史可以追溯到早期欧洲移民将它们带到北美洲，最初它们被用作控制啮齿动物的"工作猫"。这种历史背景赋予了它们坚韧、机警的特质。它们的被毛护理相对简单，平均寿命可达15到20年。'
    },
    {
      name: '暹罗猫',
      image: getImage('暹罗猫'),
      traits: ['四肢修长', '蓝眼睛', '叫声响亮'],
      origin: '泰国',
      weight: '2.5-5.5kg',
      description: '暹罗猫以其独特的重点色图案而闻名——身体颜色较浅，而在耳朵、面部、爪子和尾巴等部位呈现较深的颜色。它们还拥有一双引人注目的蓝色杏仁形眼睛。暹罗猫的体型优雅而修长。在性格方面，它们非常聪明、活泼、善于交际且非常爱说话。暹罗猫非常依恋主人，需要大量的关注，有时甚至会非常粘人。它们通常对儿童和其他宠物也很友好。虽然它们的被毛很短，不需要太多的梳理，但由于它们精力充沛，因此需要充足的活动空间和丰富的环境。暹罗猫非常喜欢通过叫声与人交流，有些主人甚至觉得它们非常"健谈"。'
    },
    {
      name: '孟加拉猫',
      image: getImage('孟加拉猫'),
      traits: ['豹纹被毛', '肌肉发达', '活力充沛'],
      origin: '美国',
      weight: '4-7kg',
      description: '孟加拉猫以其独特的被毛而引人注目，它们的被毛呈现出类似豹子的大理石纹或斑点图案。它们的被毛非常柔软，并且拥有肌肉发达的体格。孟加拉猫通常非常活跃、好玩且好奇，即使到了成年期也常常保持着小猫般的活力。由于它们精力充沛，因此需要积极的生活方式，并需要大量的玩具和玩耍机会来保持身心健康。尽管孟加拉猫的外表看起来有些"野性"，但它们的性格通常非常活泼和顽皮，这使得它们成为那些希望拥有一个充满活力的伴侣的人的理想选择。'
    },
    {
      name: '俄罗斯蓝猫',
      image: getImage('俄罗斯蓝猫'),
      traits: ['蓝灰色被毛', '绿色眼睛', '安静优雅'],
      origin: '俄罗斯',
      weight: '3-5.5kg',
      description: '俄罗斯蓝猫拥有优雅的外观，其显著特征是短而浓密的双层被毛，呈现出独特的蓝灰色，并带有银色的光泽。它们通常拥有一双迷人的黄绿色眼睛，并且面部线条有时看起来像是在微笑。在性格方面，俄罗斯蓝猫通常安静而温柔，但对家人非常忠诚和友善。它们有时会对陌生人比较害羞。俄罗斯蓝猫相对来说不需要太多的被毛护理。它们非常聪明，需要玩具和游戏来提供心理上的刺激。由于它们安静的性格和对熟悉环境的偏爱，俄罗斯蓝猫非常适合那些寻求一个安静但仍然充满爱意的伴侣的个人或家庭。'
    },
    {
      name: "阿比西尼亚猫",
      image: getImage('阿比西尼亚猫'),
      traits: ["楔形头部", "大耳朵", "活泼好动"],
      origin: "埃塞俄比亚",
      weight: "4-7.5kg",
      description: "阿比西尼亚猫拥有独特而美丽的被毛，被称为“滴答”虎斑纹，即每根毛发上都有不同颜色的条带，赋予它们一种丰富的、闪闪发光的 appearance 。它们的体型优雅而肌肉发达，外观常常让人联想到古埃及的猫 。阿比西尼亚猫以其活泼、好奇、聪明和爱玩的性格而闻名 。它们非常活跃，喜欢探索周围的环境，并且通常会与对它们最好的人建立深厚的感情 。由于它们互动性强，有时甚至被称为“狗狗猫”，但它们通常不是喜欢长时间坐在人腿上的猫 。它们的短被毛非常容易打理 。阿比西尼亚猫的这种高度互动性和对环境的兴趣意味着它们需要一个能够提供足够刺激和关注的家庭。"
    },
    {
        name: "斯芬克斯猫",
        image: getImage('斯芬克斯猫'),
        traits: ["无毛皮肤", "大耳朵", "体温敏感"],
        origin: "加拿大",
        weight: "3.5-7kg",
        description: "斯芬克斯猫以其无毛的外观而著称，它们的皮肤布满了皱纹，并拥有大的耳朵和眼睛以及突出的颧骨 。尽管外表独特，但它们的性格却非常友好、活泼、爱玩且喜欢成为人们关注的中心 。它们是典型的“人来疯”猫 ，并且通常能与其他猫和狗相处融洽 。由于没有毛发，斯芬克斯猫对温度变化非常敏感，既怕冷又怕热，因此需要主人的特别保护 。与大多数猫不同的是，它们会出汗，并且对阳光敏感 ，因此更适合室内饲养。它们的皮肤容易堆积油脂，需要定期清洁 。此外，它们的食量通常比其他有毛的猫要大 。斯芬克斯猫的独特外表和充满爱意的性格使得它们成为那些愿意满足其特殊需求的猫咪爱好者的理想选择。"
    },
    {
        name: "德文卷毛猫",
        image: getImage('德文卷毛猫'),
        traits: ["卷曲短毛", "大耳朵", "高智商"],
        origin: "英国",
        weight: "3-4kg",
        description: "德文卷毛猫拥有独特的波浪状或卷曲的被毛，甚至它们的胡须也是卷曲的 。它们通常拥有大的耳朵，并且常常被形容为外表像小精灵或外星人 。在性格方面，德文卷毛猫热情、活泼、好奇且非常深情 。它们非常聪明，容易训练 ，并且通常与人和动物都相处得很好 。与一些其他短毛猫相比，德文卷毛猫掉毛较少 。由于它们的大耳朵容易积聚油脂，因此定期清洁耳朵非常重要 。它们有时也被描述为行为更像狗而不是猫 ，这表明它们具有高度的互动性和对主人的依恋。"
    }
  ],
  longHair: [
    {
      name: '波斯猫',
      image: getImage('波斯猫'),
      traits: ['长毛华丽', '扁平面孔', '安静优雅'],
      origin: '伊朗',
      weight: '3-6kg',
      description: '波斯猫是世界上最古老也最有名气的品种之一。它们以其长而奢华的被毛、圆脸、短鼻子和大的、富有表现力的眼睛而闻名。波斯猫的毛色种类繁多。在性格方面，它们通常温柔、安静且深情。它们喜欢平静的环境，并且不太活跃。波斯猫需要大量的被毛护理，包括每天梳理以防止打结和缠绕，以及定期清洁眼睛，因为它们的扁平脸部结构可能会导致泪腺问题。由于它们的脸部结构，一些波斯猫可能会出现呼吸道问题。波斯猫的优雅外表和甜美的性格使它们成为许多猫咪爱好者的理想选择，但潜在的主人需要准备好投入大量的时间进行日常护理。'
    },
    {
      name: '缅因猫',
      image: getImage('缅因猫'),
      traits: ['体型巨大', '长毛蓬松', '温和友善'],
      origin: '美国',
      weight: '5.5-11kg',
      description: '缅因猫是体型最大的家猫品种之一，常常被称为"温柔的巨人"。它们拥有蓬松的中长被毛，一条浓密的尾巴，以及常常带有耳尖饰毛的耳朵。缅因猫的性格通常非常温和、友善和聪明。它们喜欢与家人亲近，并且对儿童和其他宠物通常都很友好。尽管它们的被毛浓密，但它们通常能够适应不同的气候。然而，为了防止毛发打结，定期梳理仍然是必不可少的。缅因猫也以其爱玩耍的性格而闻名，需要足够的活动空间和玩具。它们庞大的体型和友好的性格使它们成为许多家庭的理想宠物。'
    },
    {
      name: "布偶猫",
      image: getImage('布偶猫'),
      traits: ["蓝眼睛", "长毛柔软", "温顺粘人"],
      origin: "美国",
      weight: "5.4-9.1kg",
      description: "布偶猫是一种大型且蓬松的猫，拥有引人注目的蓝色眼睛和重点色的被毛图案 。它们的毛发柔软而丝滑。布偶猫最著名的特征之一是，当被抱起来时，它们会完全放松，像一个布娃娃一样变得软绵绵的，这也是它们名字的由来 。在性格方面，布偶猫非常深情、温柔、随和且容易相处 。它们非常忠诚，常常会跟随主人，因此有时被称为“小狗猫”。布偶猫通常对儿童和其他宠物都非常宽容 。它们的长毛需要定期梳理以防止打结 。布偶猫温顺的性格和美丽的外表使它们成为非常受欢迎的宠物。"
    },
    {
      name: "喜马拉雅猫",
      image: getImage('喜马拉雅猫'),
      traits: ["短鼻圆脸", "重点色毛", "蓝眼睛"],
      origin: "英国/美国",
      weight: "3-6kg",
      description: "喜马拉雅猫本质上是拥有暹罗猫重点色图案的波斯猫 。它们拥有波斯猫的体型和长而飘逸的被毛，以及暹罗猫标志性的蓝色眼睛和重点色。在性格方面，喜马拉雅猫通常甜美、深情且安静 。与波斯猫一样，它们的长毛需要定期且彻底的梳理 。根据一个来源 ，喜马拉雅猫拥有波斯猫的身体和头部类型，但被毛是短而柔软的（这与其他来源认为它们是长毛猫的说法相矛盾，可能需要进一步核实）。无论毛发长度如何，可以肯定的是，喜马拉雅猫都需要定期梳理以保持其被毛的健康和光泽。它们的结合了波斯猫的优雅和暹罗猫的独特色彩，使其成为一种非常吸引人的品种。"
    },
    {
      name: "挪威森林猫",
      image: getImage('挪威森林猫'),
      traits: ["双层防水毛", "大脚掌", "攀爬能手"],
      origin: "挪威",
      weight: "4-9kg",
      origin: "挪威",
      weight: "4-9kg",
      description: "挪威森林猫是一种大型、肌肉发达的猫，拥有浓密且防水的双层被毛，这使它们能够适应寒冷的气候 。它们拥有浓密的尾巴和带有饰毛的耳朵 。在性格方面，挪威森林猫通常适应性强、爱玩且友好 。它们与儿童和其他宠物相处融洽 。它们的厚被毛需要定期梳理，尤其是在换毛季节 。有趣的是，挪威森林猫在挪威神话中有着悠久的历史 。它们强壮的体格和适应寒冷气候的能力表明，它们是一种坚韧且适应性强的品种。"
    },
    {
      name: "土耳其安哥拉猫",
      image: getImage('土耳其安哥拉猫'),
      traits: ["丝质长毛", "杏仁眼", "爱玩水"],
      origin: "土耳其",
      weight: "3.5-7kg",
      description: "土耳其安哥拉猫是最古老的长毛猫品种之一，16世纪传入欧洲后风靡一时。传统毛色为白色，现多样化发展。性格独立优雅，动作敏捷且喜欢游泳，但对温度敏感。需注意白色蓝眼个体易患耳聋，平均寿命15-20年，日常需定期梳理毛发防止打结。"
    }
  ],
  special: [
    {
      name: '苏格兰折耳猫',
      image: getImage('苏格兰折耳猫'),
      traits: ['折叠耳朵', '圆脸大眼', '安静温顺'],
      origin: '苏格兰',
      weight: '3-6kg',
      description: '苏格兰折耳猫最显著的特征是它们独特的折叠耳朵，这使它们看起来像小猫头鹰。它们的耳朵向前弯曲，贴近头部。苏格兰折耳猫可以拥有短毛或长毛。在性格方面，它们通常性格甜美、温柔且友好。它们与人和动物都相处得很好。苏格兰折耳猫以其柔和的声音和各种独特的咕噜声和喵叫声而闻名。值得注意的是，它们的折叠耳朵是由于一种基因突变造成的，这种突变有时可能与软骨问题有关。'
    },
    {
      "name": "异国短毛猫",
      "image": getImage('异国短毛猫'),
      "traits": ["圆脸扁鼻", "短毛波斯", "性格温顺"],
      "origin": "美国",
      "weight": "3–6.5kg",
      "description": "异国短毛猫是波斯猫的短毛版本，拥有圆润的脸庞和短而浓密的被毛。它们性格温和、友善，适应能力强，适合家庭饲养。由于泪腺发达，需定期清洁眼部。"
    },
    {
      "name": "美国卷耳猫",
      "image": getImage('美国卷耳猫'),
      "traits": ["耳朵向后卷曲", "亲人友好", "活泼好奇"],
      "origin": "美国",
      "weight": "3–5kg",
      "description": "美国卷耳猫以其独特的向后卷曲的耳朵而闻名，性格友好、活泼，喜欢与人互动，适应能力强，适合家庭生活。"
    },
    {
      "name": "曼基康猫",
      "image": getImage('曼基康猫'),
      "traits": ["短腿", "活泼好动", "亲人"],
      "origin": "美国",
      "weight": "2.5–4kg",
      "description": "曼基康猫以其短腿著称，性格活泼、好奇，友善，喜欢与人互动，适合家庭饲养。被称为“猫界的柯基”，深受人们喜爱。"
    },
    {
      "name": "西伯利亚猫",
      "image": getImage('西伯利亚猫'),
      "traits": ["三层厚被毛", "体型强壮", "性格温和"],
      "origin": "俄罗斯",
      "weight": "4.5–9kg",
      "description": "西伯利亚猫是俄罗斯的国猫，拥有浓密的三层被毛和温和的性格，适应寒冷气候，是理想的家庭宠物。"
    }
  ]
};

// 表格数据
const tableData = [
  {
    breed: '中国狸花猫',
    appearance: '虎斑状被毛，体格健壮，肌肉发达',
    personality: '聪明，活泼，捕鼠能力强，适应性强',
    care: '低维护，易于饲养，无特殊护理需求'
  },
  {
    breed: '山东狮子猫',
    appearance: '长毛，通常为白色，部分具有异色瞳（鸳鸯眼）',
    personality: '温顺，优雅，亲人，曾是皇室宠物',
    care: '中等维护，需要定期梳理长毛'
  },
  {
    breed: '四川简州猫',
    appearance: '耳朵轮廓重叠，似有四耳，体型健壮',
    personality: '机警，灵活，独立性强',
    care: '低维护，适应性强，易于饲养'
  },
  {
    breed: '玄猫',
    appearance: '全身黑色，被视为灵猫，体型中等',
    personality: '神秘，独立，在传统文化中象征辟邪和吉祥',
    care: '低维护，无特殊护理需求'
  },
  {
    breed: '黄狸猫',
    appearance: '橘黄色被毛，体型中等，肌肉结实',
    personality: '温顺，亲人，食量大，易于饲养',
    care: '低维护，适合初次养猫的家庭'
  },
  {
    breed: '三花猫',
    appearance: '三色被毛（黑、白、橙），多为雌性',
    personality: '聪明，活泼，在中国传统文化中被视为能带来好运',
    care: '低维护，无特殊护理需求'
  },
  {
    breed: '美国短毛猫',
    appearance: '健壮，短而密的被毛，多种颜色（银虎斑著名）',
    personality: '温顺，友善，聪明，适应性强，适合家庭',
    care: '低维护，定期梳理'
  },
  {
    breed: '英国短毛猫',
    appearance: '矮胖，短而密的被毛，多种颜色（蓝色著名）',
    personality: '随和，友善，安静，喜欢与人亲近，适合公寓',
    care: '中等维护，定期梳理，注意体重'
  },
  {
    breed: '暹罗猫',
    appearance: '重点色，蓝色杏仁眼，优雅修长',
    personality: '聪明，活泼，爱说话，黏人，需要关注',
    care: '低维护，需要充足的活动空间'
  },
  {
    breed: '孟加拉猫',
    appearance: '大理石纹或豹点被毛，肌肉发达',
    personality: '活跃，好玩，好奇',
    care: '需要活跃的生活方式和充足的刺激'
  },
  {
    breed: '俄罗斯蓝猫',
    appearance: '短而密的双层被毛（蓝灰色），黄绿色眼睛，微笑表情',
    personality: '安静，温柔，忠诚，对家人友好，对陌生人害羞',
    care: '低维护，需要玩具进行心理刺激'
  },
  {
    breed: '阿比西尼亚猫',
    appearance: '斑点虎斑被毛，金色光泽眼睛，类似古埃及猫',
    personality: '活泼，好奇，聪明，好玩，被称为“狗狗猫”，不爱抱',
    care: '低维护'
  },
  {
    breed: '斯芬克斯猫',
    appearance: '无毛，皮肤有褶皱，大耳朵',
    personality: '充满活力，爱玩，爱撒娇，喜欢成为关注中心',
    care: '高维护，需要保暖防晒，定期清洁皮肤，食量较大'
  },
  {
    breed: '德文卷毛猫',
    appearance: '波浪状卷曲被毛（包括胡须），大耳朵，精灵般',
    personality: '热情，活泼，聪明，好奇，黏人，被称为“猫界的E型人格”',
    care: '低维护，掉毛少，需要定期清洁耳朵'
  },
  {
    breed: '缅因猫',
    appearance: '大型，蓬松，中长被毛，浓密的尾巴',
    personality: '温柔，友善，聪明，适合家庭',
    care: '中等维护，需要定期梳理，注意饮食管理'
  },
  {
    breed: '布偶猫',
    appearance: '大型，蓬松，重点色被毛，蓝眼睛',
    personality: '友善，温柔，随和，忠诚，黏人，被称为“小狗猫”',
    care: '中等维护，需要定期梳理'
  },
  {
    breed: '波斯猫',
    appearance: '长而奢华的被毛，圆脸，短鼻子',
    personality: '温柔，安静，深情，喜欢平静的环境',
    care: '高维护，需要每天梳理和清理眼睛，注意呼吸道问题'
  },
  {
    breed: '喜马拉雅猫',
    appearance: '波斯猫体型，暹罗猫重点色',
    personality: '甜美，深情，安静',
    care: '高维护，需要定期梳理'
  },
  {
    breed: '挪威森林猫',
    appearance: '大型，强壮，双层厚被毛',
    personality: '活跃，好玩，友好，适合儿童和其他宠物',
    care: '中等维护，需要定期梳理'
  },
  {
    breed: '土耳其安哥拉猫',
    appearance: '中等体型，修长优雅，丝滑长毛，常见白色，眼睛为蓝色、绿色或异色瞳',
    personality: '聪明，活泼，亲人，喜欢互动，适合家庭生活',
    care: '中等维护，定期梳理毛发，注意心脏健康，提供丰富的活动空间'
  }
];

const CatBreedsPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBreed, setSelectedBreed] = useState(null);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenModal = (breed) => {
    setSelectedBreed(breed);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // 根据当前选项卡获取猫咪品种数据
  const getBreedsByTab = () => {
    switch(tabValue) {
      case 0: return catBreeds.chineseBreeds;
      case 1: return catBreeds.shortHair;
      case 2: return catBreeds.longHair;
      case 3: return catBreeds.special;
      default: return [];
    }
  };

  // 获取当前选项卡的标题
  const getTabTitle = () => {
    switch(tabValue) {
      case 0: return '中国特有品种';
      case 1: return '短毛猫品种';
      case 2: return '长毛猫品种';
      case 3: return '特色猫品种';
      case 4: return '品种对比';
      default: return '';
    }
  };

  // 获取当前选项卡的描述
  const getTabDescription = () => {
    switch(tabValue) {
      case 0: 
        return '中国特有的猫咪品种拥有悠久的历史和独特的特征。这些品种在中国文化中占有重要地位，有些甚至曾是皇室宠物。它们不仅外观独特，而且通常具有适应中国各种环境的能力。';
      case 1: 
        return '短毛猫最显著的特征是它们短而浓密的被毛。与长毛猫相比，短毛猫通常需要较少的梳理，这使得它们对于那些时间有限或不喜欢频繁宠物美容的人来说是一个不错的选择。';
      case 2: 
        return '长毛猫以其长而飘逸的被毛为特征，这赋予了它们一种优雅和奢华的外观。然而，这种美丽的被毛通常需要比短毛猫更多的梳理，以防止打结和缠绕。';
      case 3: 
        return '除了常见的短毛猫和长毛猫品种外，还有一些具有独特特征的猫咪品种。这些特色品种通常因其独特的外观或特殊的性格特点而受到关注。';
      case 4: 
        return '下表展示了不同猫咪品种的主要特征对比，帮助您选择最适合的猫咪伴侣。';
      default: 
        return '';
    }
  };

  return (
    <ThemeProvider theme={catTheme}>
      <div style={styles.container}>
        {/* 页面标题和介绍 */}
        <Typography variant="h4" gutterBottom color="primary" sx={styles.pageTitle}>
          常见猫咪品种
        </Typography>
        
        <Typography variant="body2" color="primary" paragraph sx={{ fontSize: '0.85rem', lineHeight: 1.5, mb: 2 }}>
          欢迎来到我们的常见猫咪品种页面。我们创建此页面旨在为您提供全面的信息，帮助您了解各种猫咪品种的独特特征。我们深知，为您的家庭选择一只合适的猫咪是一个重要的决定，而理解不同品种的特点可以大大提高您成功领养到理想伴侣的机会。
        </Typography>

        {/* 分类标签页 */}
        <Paper sx={{ mb: 2 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth" 
            sx={{ 
              minHeight: '36px', 
              '& .MuiTab-root': { 
                minHeight: '36px', 
                fontSize: '0.8rem',
                py: 0.5
              } 
            }}
          >
            <Tab label="中国特有品种" />
            <Tab label="短毛猫品种" />
            <Tab label="长毛猫品种" />
            <Tab label="特色猫品种" />
            <Tab label="品种对比" />
          </Tabs>
        </Paper>

        {/* 标签页内容 */}
        {tabValue !== 4 ? (
          <>
            <Typography variant="body2" color="primary" paragraph sx={{ fontSize: '0.85rem', lineHeight: 1.5, mb: 2 }}>
              {getTabDescription()}
            </Typography>
            
            <Grid container spacing={2} sx={styles.gridContainer}>
              {getBreedsByTab().map((breed, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <BreedCard 
                    onClick={() => handleOpenModal(breed)}
                    sx={{ cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)' } }}
                  >
                    <CardMedia
                      component="img"
                      height="120"
                      image={breed.image}
                      alt={breed.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={styles.cardContent}>
                      <Typography variant="h6" color="secondary" gutterBottom sx={styles.cardTitle}>
                        {breed.name}
                      </Typography>
                      <Typography variant="body2" color="primary" sx={styles.cardText}>
                        <strong>原产地:</strong> {breed.origin}
                      </Typography>
                      <Typography variant="body2" color="primary" sx={styles.cardText}>
                        <strong>体重:</strong> {breed.weight}
                      </Typography>
                      <Box sx={styles.traitsContainer}>
                        {breed.traits.map((trait, i) => (
                          <Chip 
                            key={i} 
                            label={trait} 
                            size="small" 
                            sx={styles.traitChip} 
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </BreedCard>
                </Grid>
              ))}
            </Grid>

            {/* Modal for breed details */}
            <Modal
              open={modalOpen}
              onClose={handleCloseModal}
              closeAfterTransition
              BackdropComponent={Backdrop}
              BackdropProps={{
                timeout: 500,
              }}
            >
              <Fade in={modalOpen}>
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: '90%', sm: '80%', md: '60%' },
                  maxWidth: 600,
                  bgcolor: 'background.paper',
                  boxShadow: 24,
                  p: 3,
                  borderRadius: 2,
                  maxHeight: '90vh',
                  overflow: 'auto'
                }}>
                  {selectedBreed && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h5" color="secondary" sx={{ fontWeight: 600 }}>
                          {selectedBreed.name} 详细介绍
                        </Typography>
                        <CloseIcon 
                          onClick={handleCloseModal} 
                          sx={{ cursor: 'pointer', color: 'text.secondary' }} 
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
                        <Box sx={{ width: { xs: '100%', sm: '40%' }, mb: { xs: 2, sm: 0 } }}>
                          <img 
                            src={selectedBreed.image} 
                            alt={selectedBreed.name} 
                            style={{ width: '100%', borderRadius: 8, objectFit: 'cover' }} 
                          />
                        </Box>
                        <Box sx={{ width: { xs: '100%', sm: '60%' }, pl: { xs: 0, sm: 2 } }}>
                          <Typography variant="body1" color="primary" sx={{ mb: 1 }}>
                            <strong>原产地:</strong> {selectedBreed.origin}
                          </Typography>
                          <Typography variant="body1" color="primary" sx={{ mb: 1 }}>
                            <strong>体重:</strong> {selectedBreed.weight}
                          </Typography>
                          <Typography variant="body1" color="primary" sx={{ mb: 1 }}>
                            <strong>特点:</strong>
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {selectedBreed.traits.map((trait, i) => (
                              <Chip 
                                key={i} 
                                label={trait} 
                                size="small" 
                                sx={styles.traitChip} 
                              />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                      
                      <Typography variant="body1" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                        详细描述:
                      </Typography>
                      <Typography variant="body2" color="primary" sx={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
                        {selectedBreed.description}
                      </Typography>
                    </>
                  )}
                </Box>
              </Fade>
            </Modal>
          </>
        ) : (
          <>
            <Typography variant="body2" color="primary" paragraph sx={{ fontSize: '0.85rem', lineHeight: 1.5, mb: 2 }}>
              {getTabDescription()}
            </Typography>
            
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(162,100,56,0.1)' }}>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>品种</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>外观</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>性格</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>护理</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map((row, index) => (
                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(162,100,56,0.03)' } }}>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{row.breed}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{row.appearance}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{row.personality}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{row.care}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Typography variant="body2" color="primary" sx={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
            选择一只适合您家庭的猫咪是一个需要仔细考虑的决定。通过了解不同猫咪品种的外观、性格和护理需求，您可以更好地找到一个能够与您的生活方式和喜好相匹配的伴侣。我们希望本页面提供的信息能够帮助您做出明智的决定。我们鼓励您花时间与我们救助站的猫咪互动，以便找到最适合您的那一只。记住，每一只猫都是独一无二的，无论品种如何，它们都能为您的生活带来无尽的爱和陪伴。
            </Typography>
          </>
        )}
      </div>
    </ThemeProvider>
  );
};

export default CatBreedsPage;
